import { Option } from '@/components/Form';
import { useFilterConditionAtom } from '@/features/analyze/atoms/filterCondition.atom';
import { useFacet } from '@/features/analyze/hooks/useFacet';
import {
  AllowedFacetConditionOperator,
  FacetKey,
} from '@/features/analyze/types';
import { useTranslationDictionary } from '@/lib/i18n/useTranslationDictionary';
import { useCallback, useState } from 'react';
import { MultiValue } from 'react-select';
import { getFacetConditionValues } from '../lib/facetConditionUtils';

export function useFacetSelect<K extends FacetKey>(
  facetKey: K,
  operator: AllowedFacetConditionOperator<K>
) {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [filterCondition, setFilterCondition] = useFilterConditionAtom();
  const facetConditionValues = getFacetConditionValues(
    facetKey,
    operator,
    filterCondition
  );

  const isTopicTagFacet = facetKey === 'topicTags';
  const isThemeFacet = facetKey === 'themes';
  const { getLabel: getTopicTagLabel, isLoading: isTopicTagDictionaryLoading } =
    useTranslationDictionary('topic_tag', {
      enabled: isTopicTagFacet,
    });
  const { getLabel: getThemeLabel, isLoading: isThemeDictionaryLoading } =
    useTranslationDictionary('theme', {
      enabled: isThemeFacet,
    });

  const { facets, isLoading: isFacetLoading } = useFacet({
    facetKey,
    // メニューが開いたら or 開いている時はリクエストするようして無駄なリクエストを送らないようにする
    sendRequest: isMenuOpen,
    // or条件時はfilterConditionを空にして全facet取得
    ignoreFilterCondition: operator === 'or',
  });

  const isLoading = isFacetLoading;

  const formatLabel = (value: string, count?: number) => {
    let label: string | undefined;

    if (isThemeFacet) {
      label = getThemeLabel(value);
    } else if (isTopicTagFacet) {
      label = getTopicTagLabel(value);
    }
    label ??= value;

    return count != null ? `${label} (${count})` : String(label);
  };

  const options: Option[] = facets.map((facet) => ({
    value: facet.value,
    label: formatLabel(facet.value, facet.count),
  }));

  const selectedOptions: Option[] | undefined = facetConditionValues?.map(
    (value) => ({
      value,
      label: formatLabel(value),
    })
  );

  const onChangeSelectedFacets = useCallback(
    (newValue: MultiValue<Option>) => {
      setFilterCondition((prev) => ({
        ...prev,
        [facetKey]: {
          ...prev[facetKey],
          [operator]: newValue.map(({ value }) => value),
        },
      }));
    },
    [operator, setFilterCondition, facetKey]
  );

  const handleMenuOpen = useCallback(() => {
    setIsMenuOpen(true);
  }, []);

  const handleMenuClose = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  return {
    options,
    selectedOptions,
    isLoading:
      isLoading ||
      (isTopicTagFacet ? isTopicTagDictionaryLoading : false) ||
      (isThemeFacet ? isThemeDictionaryLoading : false),
    onChangeSelectedFacets,
    handleMenuOpen,
    handleMenuClose,
  };
}
